import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { ConfirmDialog } from '../components/DetailedView/ConfirmDialog'
import { registerDraftGuard } from '../utils/draftGuardRegistry'
import { registerSwitchGuard } from '../services/workspaceSwitch/switchGuards'
import { getNlsString } from '../nls/nlsRuntime'
import type {
  DraftRecord,
  ManageItemKind,
  ManageItemState,
} from '../types/manageDraftState'
import {
  ManageDraftContext,
  type DiscardAllHandler,
  type ExternalPendingSource,
  type LeaveDialogRequest,
  type ManageDraftContextValue,
  type SaveAllHandler,
} from './manageDraftContextCore'

export const ManageDraftProvider = ({ children }: { children: ReactNode }) => {
  const [records, setRecords] = useState<Map<string, DraftRecord>>(() => new Map())
  const [leaveRequest, setLeaveRequest] = useState<LeaveDialogRequest | null>(null)
  const handlersRef = useRef<{
    saveAll?: SaveAllHandler
    discardAll?: DiscardAllHandler
  }>({})
  // External sources (e.g. Review's DetailedViewScreen) that carry their own
  // pending state. Kept in state (not a ref) so hasUnsavedDrafts, the leave
  // dialog body, and beforeunload react whenever a source is registered or
  // its `hasUnsaved` boolean flips.
  const [externalSources, setExternalSources] = useState<Map<string, ExternalPendingSource>>(() => new Map())
  const anyExternalPending = useMemo(() => {
    for (const source of externalSources.values()) {
      if (source.hasUnsaved) return true
    }
    return false
  }, [externalSources])

  const hasUnsavedDrafts = records.size > 0 || anyExternalPending
  const { draftCount, modifiedCount } = useMemo(() => {
    let d = 0
    let m = 0
    records.forEach((r) => {
      if (r.state === 'draft') d += 1
      else m += 1
    })
    return { draftCount: d, modifiedCount: m }
  }, [records])

  const getState = useCallback(
    (id: string): ManageItemState => records.get(id)?.state ?? 'saved',
    [records],
  )
  const getRecord = useCallback((id: string) => records.get(id), [records])

  const markDraft = useCallback((record: Omit<DraftRecord, 'state'>) => {
    setRecords((current) => {
      const next = new Map(current)
      next.set(record.id, { ...record, state: 'draft' })
      return next
    })
  }, [])

  const markModified = useCallback(
    (
      id: string,
      kind: ManageItemKind,
      parentId: string | null,
      originalSnapshot: unknown,
    ) => {
      setRecords((current) => {
        // If the item is already a draft, keep it as draft — modifying a draft
        // is still a draft, not "modified saved".
        const existing = current.get(id)
        if (existing?.state === 'draft') return current
        // If it's already modified, don't overwrite the original snapshot —
        // the first edit's snapshot is what discard should revert to.
        if (existing?.state === 'modified') return current
        const next = new Map(current)
        next.set(id, {
          id,
          kind,
          parentId,
          state: 'modified',
          originalSnapshot,
        })
        return next
      })
    },
    [],
  )

  const seedProposedEdits = useCallback(
    (edits: ReadonlyArray<{
      id: string
      kind: ManageItemKind
      parentId: string | null
      originalSnapshot: unknown
      proposedFields: Record<string, unknown>
    }>) => {
      if (edits.length === 0) return
      setRecords((current) => {
        const next = new Map(current)
        edits.forEach((edit) => {
          // Overwrite unconditionally — a fresh AI run replaces prior
          // proposals for the same id. Users still see the diff (originalSnapshot
          // vs proposedFields) and can Save or Revert.
          next.set(edit.id, {
            id: edit.id,
            kind: edit.kind,
            parentId: edit.parentId,
            state: 'modified',
            originalSnapshot: edit.originalSnapshot,
            proposedFields: edit.proposedFields,
          })
        })
        return next
      })
    },
    [],
  )

  const markSaved = useCallback((id: string) => {
    setRecords((current) => {
      if (!current.has(id)) return current
      const next = new Map(current)
      next.delete(id)
      return next
    })
  }, [])

  const markSavedMany = useCallback((ids: ReadonlyArray<string>) => {
    setRecords((current) => {
      let mutated = false
      const next = new Map(current)
      ids.forEach((id) => {
        if (next.delete(id)) mutated = true
      })
      return mutated ? next : current
    })
  }, [])

  const clearRecord = useCallback((id: string) => {
    setRecords((current) => {
      if (!current.has(id)) return current
      const next = new Map(current)
      next.delete(id)
      return next
    })
  }, [])

  const clearMany = useCallback((ids: ReadonlyArray<string>) => {
    setRecords((current) => {
      let mutated = false
      const next = new Map(current)
      ids.forEach((id) => {
        if (next.delete(id)) mutated = true
      })
      return mutated ? next : current
    })
  }, [])

  const clearAll = useCallback(() => {
    setRecords((current) => (current.size === 0 ? current : new Map()))
  }, [])

  const setHandlers = useCallback(
    (handlers: { saveAll?: SaveAllHandler; discardAll?: DiscardAllHandler }) => {
      handlersRef.current = {
        saveAll: handlers.saveAll ?? handlersRef.current.saveAll,
        discardAll: handlers.discardAll ?? handlersRef.current.discardAll,
      }
    },
    [],
  )

  const registerExternalPendingSource = useCallback(
    (key: string, source: ExternalPendingSource) => {
      setExternalSources((current) => {
        const existing = current.get(key)
        if (
          existing &&
          existing.hasUnsaved === source.hasUnsaved &&
          existing.saveAll === source.saveAll &&
          existing.discardAll === source.discardAll
        ) {
          return current
        }
        const next = new Map(current)
        next.set(key, source)
        return next
      })
      return () => {
        setExternalSources((current) => {
          if (!current.has(key)) return current
          const next = new Map(current)
          next.delete(key)
          return next
        })
      }
    },
    [],
  )

  const guardLeave = useCallback(async (): Promise<boolean> => {
    if (records.size === 0 && !anyExternalPending) return true
    return await new Promise<boolean>((resolve) => {
      setLeaveRequest({ resolve })
    })
  }, [records, anyExternalPending])

  // Register guard against logout / app-level navigation flows.
  useEffect(() => {
    return registerDraftGuard(guardLeave)
  }, [guardLeave])

  // Register switch guard against workspace switching.
  useEffect(() => {
    return registerSwitchGuard({
      id: 'manage-drafts',
      describe: () => {
        if (records.size === 0 && !anyExternalPending) return null
        const total = (draftCount || 0) + (modifiedCount || 0)
        if (total > 1) {
          return getNlsString('NLSManageDraftsGuardDescription', { count: total })
        }
        return getNlsString('NLSManageDraftsGuardDescriptionSingle')
      },
      save: async () => {
        const fn = handlersRef.current.saveAll
        if (fn) await fn()
        for (const source of externalSources.values()) {
          if (source.hasUnsaved) await source.saveAll()
        }
      },
      discard: async () => {
        const fn = handlersRef.current.discardAll
        if (fn) await fn()
        else clearAll()
        for (const source of externalSources.values()) {
          if (source.hasUnsaved) await source.discardAll()
        }
      },
    })
  }, [records.size, anyExternalPending, draftCount, modifiedCount, externalSources, clearAll])

  // Beforeunload: warn on tab close / refresh when drafts exist.
  useEffect(() => {
    if (!hasUnsavedDrafts) return
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [hasUnsavedDrafts])

  const handleLeaveSaveAll = useCallback(async () => {
    const req = leaveRequest
    if (!req) return
    try {
      const fn = handlersRef.current.saveAll
      if (fn) await fn()
      // Fan out to every registered secondary view (e.g. Review) so its
      // pending state persists too. Sequential to preserve save-order
      // guarantees the primary handler relies on.
      for (const source of externalSources.values()) {
        if (source.hasUnsaved) await source.saveAll()
      }
    } finally {
      setLeaveRequest(null)
      req.resolve(true)
    }
  }, [leaveRequest, externalSources])

  const handleLeaveDiscardAll = useCallback(async () => {
    const req = leaveRequest
    if (!req) return
    try {
      const fn = handlersRef.current.discardAll
      if (fn) await fn()
      else clearAll()
      for (const source of externalSources.values()) {
        if (source.hasUnsaved) await source.discardAll()
      }
    } finally {
      setLeaveRequest(null)
      req.resolve(true)
    }
  }, [leaveRequest, clearAll, externalSources])

  const handleLeaveCancel = useCallback(() => {
    const req = leaveRequest
    if (!req) return
    setLeaveRequest(null)
    req.resolve(false)
  }, [leaveRequest])

  const value = useMemo<ManageDraftContextValue>(
    () => ({
      records,
      hasUnsavedDrafts,
      draftCount,
      modifiedCount,
      getState,
      getRecord,
      markDraft,
      markModified,
      seedProposedEdits,
      markSaved,
      markSavedMany,
      clearRecord,
      clearMany,
      clearAll,
      setHandlers,
      registerExternalPendingSource,
      guardLeave,
    }),
    [
      records,
      hasUnsavedDrafts,
      draftCount,
      modifiedCount,
      getState,
      getRecord,
      markDraft,
      markModified,
      seedProposedEdits,
      markSaved,
      markSavedMany,
      clearRecord,
      clearMany,
      clearAll,
      setHandlers,
      registerExternalPendingSource,
      guardLeave,
    ],
  )

  const leaveBody = useMemo(() => {
    // External sources (e.g. Review) may contribute pending state without a
    // draft/modified count. Fall back to a generic body when we only know a
    // secondary view has changes.
    if (draftCount && modifiedCount) {
      return `You have ${draftCount} unsaved draft${draftCount === 1 ? '' : 's'} and ${modifiedCount} edited item${modifiedCount === 1 ? '' : 's'} on Manage. What should we do before leaving?`
    }
    if (draftCount) {
      return `You have ${draftCount} unsaved draft${draftCount === 1 ? '' : 's'} on Manage. What should we do before leaving?`
    }
    if (modifiedCount) {
      return `You have ${modifiedCount} edited item${modifiedCount === 1 ? '' : 's'} on Manage. What should we do before leaving?`
    }
    return 'You have unsaved changes on Manage. What should we do before leaving?'
  }, [draftCount, modifiedCount])

  return (
    <ManageDraftContext.Provider value={value}>
      {children}
      {leaveRequest ? (
        <ConfirmDialog
          title="Unsaved changes on Manage"
          body={leaveBody}
          confirmLabel="Save all"
          tone="neutral"
          secondaryLabel="Discard all"
          secondaryTone="danger"
          cancelLabel="Stay on Manage"
          onConfirm={() => void handleLeaveSaveAll()}
          onSecondary={() => void handleLeaveDiscardAll()}
          onCancel={handleLeaveCancel}
        />
      ) : null}
    </ManageDraftContext.Provider>
  )
}
